import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X, Sparkles, Bell, BellOff } from 'lucide-react';
import { 
  isPushSupported, 
  subscribeToPush, 
  getPushSubscription,
  requestNotificationPermission 
} from '../../utils/notifications';

/**
 * UpdatePrompt Component
 * 
 * A senior-friendly update notification that appears when a new version
 * of the app is available. Features large text, high contrast, and
 * easy-to-tap buttons.
 */
export const UpdatePrompt = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Listen for custom event dispatched from main.tsx
    const handleUpdateAvailable = (event: CustomEvent<{ worker: ServiceWorker }>) => {
      setWaitingWorker(event.detail.worker);
      setShowUpdate(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable as EventListener);

    // Also check if there's already a waiting worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowUpdate(true);
        }
      });
    }

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable as EventListener);
    };
  }, []);

  const handleUpdate = () => {
    if (!waitingWorker) return;

    setIsUpdating(true);

    // Tell the waiting service worker to skip waiting
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });

    // Listen for the controlling service worker to change
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });

    // Fallback reload after 3 seconds if controllerchange doesn't fire
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  return (
    <AnimatePresence>
      {showUpdate && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed top-4 left-4 right-4 z-[9999] md:left-auto md:right-4 md:max-w-md"
        >
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl shadow-xl overflow-hidden">
            {/* Header with close button */}
            <div className="flex items-start justify-between p-4 pb-2">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    New Version Ready!
                  </h3>
                  <p className="text-sm text-gray-600">
                    We've made improvements
                  </p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Dismiss update notification"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Message */}
            <div className="px-4 py-2">
              <p className="text-base text-gray-700">
                A new version is available with improvements and fixes. 
                Update now to get the best experience!
              </p>
            </div>

            {/* Action Buttons */}
            <div className="p-4 pt-2 flex gap-3">
              <button
                onClick={handleDismiss}
                disabled={isUpdating}
                className="flex-1 py-3 px-4 text-lg font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
              >
                Later
              </button>
              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="flex-1 py-3 px-4 text-lg font-semibold text-white bg-emerald-500 hover:bg-emerald-600 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isUpdating ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    Update Now
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * PushNotificationPrompt Component
 * 
 * Prompts users to enable push notifications with a friendly UI.
 * Shows only when notifications are not yet granted.
 */
export const PushNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // Check if push is supported and not yet subscribed
    const checkSubscription = async () => {
      if (!isPushSupported()) return;
      
      // Don't show if already denied
      if (Notification.permission === 'denied') return;
      
      // Don't show if already granted and subscribed
      if (Notification.permission === 'granted') {
        const subscription = await getPushSubscription();
        if (subscription) {
          setIsSubscribed(true);
          return;
        }
      }
      
      // Show prompt after a delay if permission is default
      if (Notification.permission === 'default') {
        const dismissed = sessionStorage.getItem('dipps_push_prompt_dismissed');
        if (!dismissed) {
          setTimeout(() => setShowPrompt(true), 5000);
        }
      }
    };
    
    checkSubscription();
  }, []);

  const handleEnable = async () => {
    setIsSubscribing(true);
    
    try {
      const permission = await requestNotificationPermission();
      
      if (permission === 'granted') {
        const subscription = await subscribeToPush();
        if (subscription) {
          setIsSubscribed(true);
          setShowPrompt(false);
        }
      }
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    sessionStorage.setItem('dipps_push_prompt_dismissed', 'true');
  };

  return (
    <AnimatePresence>
      {showPrompt && !isSubscribed && (
        <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className="fixed bottom-4 left-4 right-4 z-[9998] md:left-auto md:right-4 md:max-w-md"
      >
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between p-4 pb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Bell className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  Stay Notified! 🔔
                </h3>
                <p className="text-sm text-gray-600">
                  Never miss a reminder
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Dismiss notification prompt"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Message */}
          <div className="px-4 py-2">
            <p className="text-base text-gray-700">
              Enable notifications to receive gentle reminders for your medications, 
              water intake, and daily activities - even when the app is closed!
            </p>
          </div>

          {/* Action Buttons */}
          <div className="p-4 pt-2 flex gap-3">
            <button
              onClick={handleDismiss}
              disabled={isSubscribing}
              className="flex-1 py-3 px-4 text-lg font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
            >
              Not Now
            </button>
            <button
              onClick={handleEnable}
              disabled={isSubscribing}
              className="flex-1 py-3 px-4 text-lg font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubscribing ? (
                <>
                  <Bell className="w-5 h-5 animate-pulse" />
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-5 h-5" />
                  Enable
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>)}
    </AnimatePresence>
  );
};

export default UpdatePrompt;
